import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const initials = (n) => (n || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
const GWC_TITLES = { g: "Gets it", w: "Wants it", c: "Capacity to do it" };

function SeatCard({ seat, onEdit, isRoot }) {
  const open = !seat.person_name;
  let border = "border-slate-200 border-t-[3px] border-t-slate-800";
  if (open) border = "border-dashed border-amber-400 border-t-[3px] border-t-amber-400 bg-amber-50/40";
  else if (isRoot) border = "border-slate-200 border-t-[3px] border-t-cyan-600";
  return (
    <button onClick={() => onEdit(seat)} data-testid={`seat-card-${seat.id}`}
      className={`text-left w-[250px] bg-white border rounded-sm p-3 transition-colors hover:border-slate-400 ${border}`}>
      <div className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-slate-500">{seat.title}</div>
      <div className="flex items-center gap-2 my-1.5">
        <span className={`w-7 h-7 rounded-sm text-white text-[10px] font-bold flex items-center justify-center ${open ? "bg-amber-500" : "bg-slate-800"}`}>{open ? "?" : initials(seat.person_name)}</span>
        <span className={`text-sm font-bold ${open ? "text-amber-700" : "text-slate-900"}`} data-testid={`seat-person-${seat.id}`}>{seat.person_name || "OPEN SEAT"}</span>
      </div>
      <ul className="space-y-0.5">
        {(seat.roles || []).map((r, i) => (
          <li key={i} className="text-[11px] text-slate-600 flex items-start gap-1.5 border-t border-slate-50 pt-0.5"><span className="w-1 h-1 bg-cyan-600 mt-1.5 flex-shrink-0" />{r}</li>
        ))}
      </ul>
      <div className="flex gap-1 mt-2">
        {open ? (
          <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700">HIRING / UNFILLED</span>
        ) : (
          ["g", "w", "c"].map((k) => (
            <span key={k} title={GWC_TITLES[k]}
              className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm uppercase ${seat.gwc && seat.gwc[k] ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{k}</span>
          ))
        )}
      </div>
    </button>
  );
}

function renderTree(nodes, all, onEdit, depth) {
  if (!nodes.length) return null;
  return (
    <div className="flex flex-col items-center">
      <div className="flex flex-wrap justify-center gap-4">
        {nodes.map((n) => {
          const kids = all.filter((s) => s.parent_id === n.id).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          return (
            <div key={n.id} className="flex flex-col items-center">
              <SeatCard seat={n} onEdit={onEdit} isRoot={depth === 0} />
              {kids.length > 0 && (
                <>
                  <div className="w-px h-5 bg-slate-300" />
                  {renderTree(kids, all, onEdit, depth + 1)}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const EMPTY = { title: "", person_name: "", person_id: "", roles: "", gwc: { g: true, w: true, c: true }, parent_id: "" };

function SeatDialog({ editing, setEditing, seats, team, saveMut, delMut }) {
  const setForm = (p) => setEditing((f) => ({ ...f, ...p }));
  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
      <DialogContent className="max-w-md rounded-sm max-h-[90vh] overflow-y-auto" data-testid="seat-dialog">
        <DialogHeader><DialogTitle>{editing?.id ? "Edit Seat" : "New Seat"}</DialogTitle></DialogHeader>
        {editing && (
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Seat / function *</label>
              <Input value={editing.title} onChange={(e) => setForm({ title: e.target.value })} placeholder="e.g. Sales & Estimating" className="rounded-sm mt-1" data-testid="seat-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Who fills it</label>
                <Select value={editing.person_id || "open"} onValueChange={(val) => {
                  if (val === "open") return setForm({ person_id: "", person_name: "" });
                  const m = (team || []).find((t) => t.id === val);
                  setForm({ person_id: val, person_name: m?.name || "" });
                }}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="seat-person-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open seat (hiring)</SelectItem>
                    {(team || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Reports to</label>
                <Select value={editing.parent_id || "none"} onValueChange={(v) => setForm({ parent_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="seat-parent-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Top of chart —</SelectItem>
                    {(seats || []).filter((s) => s.id !== editing.id).map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Roles & responsibilities (one per line, max 6)</label>
              <Textarea rows={4} value={editing.roles} onChange={(e) => setForm({ roles: e.target.value })} className="rounded-sm mt-1 text-sm" data-testid="seat-roles-input" />
            </div>
            {editing.person_name && (
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">GWC — right person, right seat</label>
                <div className="flex gap-4 mt-1.5">
                  {[["g", "Gets it"], ["w", "Wants it"], ["c", "Capacity"]].map(([k, label]) => (
                    <label key={k} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input type="checkbox" checked={!!editing.gwc[k]} onChange={(e) => setForm({ gwc: { ...editing.gwc, [k]: e.target.checked } })} className="w-4 h-4 accent-emerald-600" data-testid={`gwc-${k}`} />{label}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter className="flex items-center justify-between sm:justify-between">
          {editing?.id ? (
            <Button variant="ghost" className="text-red-600 hover:bg-red-50 rounded-sm" data-testid="delete-seat-btn"
              onClick={() => window.confirm("Remove this seat? Its reports move up one level.") && delMut.mutate(editing.id)}><Trash2 size={15} className="mr-1" />Delete</Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setEditing(null)} data-testid="cancel-seat-btn">Cancel</Button>
            <Button className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="save-seat-btn"
              disabled={!editing?.title?.trim() || saveMut.isPending} onClick={() => saveMut.mutate(editing)}>
              {editing?.id ? "Save" : "Add Seat"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ManagementChart() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  const { data: seats } = useQuery({ queryKey: ["mgmt-seats"], queryFn: async () => (await api.get("/mgmt/seats")).data });
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/auth/team")).data });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["mgmt-seats"] });
  const saveMut = useMutation({
    mutationFn: async (f) => {
      const payload = {
        title: f.title, person_name: f.person_name || null, person_id: f.person_id || null,
        roles: f.roles.split("\n").map((r) => r.trim()).filter(Boolean).slice(0, 6),
        gwc: f.gwc, parent_id: f.parent_id || null,
      };
      if (f.id) return (await api.patch(`/mgmt/seats/${f.id}`, payload)).data;
      return (await api.post("/mgmt/seats", payload)).data;
    },
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Seat saved"); },
    onError: (e) => toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not save seat"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/mgmt/seats/${id}`)).data,
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Seat removed"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not delete seat"),
  });

  const openEdit = (s) => setEditing({ ...s, person_name: s.person_name || "", person_id: s.person_id || "", roles: (s.roles || []).join("\n"), parent_id: s.parent_id || "", gwc: { g: true, w: true, c: true, ...(s.gwc || {}) } });

  const roots = (seats || []).filter((s) => !s.parent_id).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const openCount = (seats || []).filter((s) => !s.person_name).length;

  return (
    <div data-testid="management-chart-page" className="pb-24">
      <PageHeader
        title="Accountability Chart"
        subtitle={`Seats & responsibilities — not titles · GWC = Gets it / Wants it / Capacity · one person can fill several seats${openCount ? ` · ${openCount} open seat${openCount > 1 ? "s" : ""}` : ""}`}
        actions={<Button onClick={() => setEditing({ ...EMPTY })} className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="add-seat-btn"><Plus size={15} className="mr-1" />Add Seat</Button>}
      />
      <div className="px-4 sm:px-6 lg:px-10">
        <div className="overflow-x-auto pt-2 pb-4" data-testid="chart-tree">
          <div className="min-w-fit mx-auto">
            {renderTree(roots, seats || [], openEdit, 0)}
          </div>
        </div>
        <div className="max-w-2xl mx-auto mt-4 bg-white border border-slate-200 border-l-[3px] border-l-cyan-600 rounded-sm px-4 py-3 text-xs text-slate-600" data-testid="chart-note">
          <b>Why seats, not titles:</b> the chart answers "who is accountable for this function". A missing GWC flag or an open seat is a structure problem — drop it on the Issues list so it surfaces in the weekly IDS instead of hiding.
        </div>
      </div>

      <SeatDialog editing={editing} setEditing={setEditing} seats={seats} team={team} saveMut={saveMut} delMut={delMut} />
    </div>
  );
}
